function marameGalleryRequestUrl(productUrl, variantId, currentHref) {
  const current = new URL(currentHref);
  const url = new URL(productUrl, current.origin);
  if (url.origin !== current.origin || !/\/products\/[^/]+\/?$/.test(url.pathname) || !/^\d+$/.test(String(variantId))) throw new Error('Invalid product');
  url.search = '';
  url.searchParams.set('variant', variantId);
  url.searchParams.set('section_id', 'marame-card-gallery');
  const preview = current.searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}

function marameValidateGallery(data, identity, origin) {
  if (!data || String(data.productId) !== identity.productId || String(data.variantId) !== identity.variantId || String(data.gallery?.colour || '') !== identity.colour || !Array.isArray(data.gallery?.images) || data.gallery.images.length > 6) throw new Error('Gallery identity mismatch');
  const images = [], seen = new Set();
  for (const image of data.gallery.images) {
    if (!image || typeof image.src !== 'string' || image.src.length > 2048) continue;
    const url = new URL(image.src, origin);
    if (!['https:', 'http:'].includes(url.protocol)) continue;
    const key = url.origin + url.pathname;
    if (seen.has(key)) continue;
    seen.add(key); images.push({ src: url.href, alt: String(image.alt || '').slice(0, 500) });
  }
  return images;
}

function marameGallerySwipe(start, end, threshold = 8) {
  if (!start || !end) return 0;
  const x = end.x - start.x, y = end.y - start.y;
  return Math.abs(x) >= threshold && Math.abs(x) > Math.abs(y) * 1.5 ? (x < 0 ? 1 : -1) : 0;
}

if (!customElements.get('marame-card-gallery')) {
  customElements.define('marame-card-gallery', class extends HTMLElement {
    static cache = new Map();
    static warmQueue = new Set();
    static warming = 0;

    static drainWarmQueue() {
      while (this.warming < 2 && this.warmQueue.size) {
        const gallery = this.warmQueue.values().next().value;
        this.warmQueue.delete(gallery);
        if (!gallery.canWarmVisible() || gallery.warmAttempted) continue;
        gallery.warmAttempted = true;
        this.warming++;
        Promise.resolve().then(() => gallery.prepare(1, true)).catch(() => {}).finally(() => {
          this.warming--;
          this.drainWarmQueue();
        });
      }
    }

    connectedCallback() {
      this.track = this.querySelector('.marame-card-gallery__track');
      if (!this.track || this.initialised && !this.controller?.signal.aborted || Number(this.dataset.count) < 2) return;
      this.initialised = true;
      this.index = this.index || 0;
      this.requestedIndex = this.index;
      this.imageLoads = new Map();
      this.readyImages ||= new Set();
      this.controller = new AbortController();
      this.setAttribute('data-controlled', '');
      const initialLink = this.track.querySelector('a');
      if (initialLink) initialLink.draggable = false;
      this.track.scrollLeft = 0;
      this.imageSizes = this.track.querySelector('img')?.sizes || '(min-width: 1280px) 25vw, (min-width: 1024px) 33vw, (min-width: 430px) 215px, 50vw';
      const options = { signal: this.controller.signal };
      this.controls?.remove(); this.dots?.remove(); this.status?.remove();
      this.makeControls(this.images?.length || Math.min(6, Number(this.dataset.count)));
      this.settleSlides();
      this.setupVisibleWarmup();
      this.track.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button > 0) return;
        // A short touch flick is enough; retain the mouse drag tolerance so
        // small movements while clicking a product still follow its link.
        this.start = { x: event.clientX, y: event.clientY, id: event.pointerId, threshold: event.pointerType === 'mouse' ? 20 : 8 };
        this.intentDirection = 0;
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.start || (this.start.id !== undefined && event.pointerId !== this.start.id)) return;
        const point = { x: event.clientX, y: event.clientY };
        const direction = marameGallerySwipe(this.start, point, 8);
        if (direction) {
          if (marameGallerySwipe(this.start, point, this.start.threshold)) this.blockClickUntil = Date.now() + 500;
          if (direction !== this.intentDirection) { this.intentDirection = direction; this.prepare(direction); }
        }
      }, options);
      this.track.addEventListener('pointerup', event => {
        if (this.start?.id !== undefined && event.pointerId !== this.start.id) return;
        const direction = marameGallerySwipe(this.start, { x: event.clientX, y: event.clientY }, this.start?.threshold);
        this.start = null;
        if (direction) { this.blockClickUntil = Date.now() + 500; this.step(direction); }
      }, options);
      this.track.addEventListener('pointercancel', () => { this.start = null; }, options);
      this.track.addEventListener('click', event => {
        if (!event.detail || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (Date.now() < (this.blockClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.track.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.addEventListener('focusin', () => this.prepare(1), options);
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.prepare(1); }, options);
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.navigationToken = (this.navigationToken || 0) + 1;
      this.controller?.abort();
      this.warmObserver?.disconnect();
      clearTimeout(this.warmTimer);
      this.constructor.warmQueue.delete(this);
      this.transition?.cancel();
      this.settleSlides();
      this.loading = null; this.navigationPending = false; this.start = null;
      this.removeAttribute('aria-busy');
      this.removeAttribute('data-gallery-loading');
    }

    makeControls(count) {
      this.controls = document.createElement('div'); this.controls.className = 'marame-card-gallery__controls';
      this.previous = document.createElement('button'); this.next = document.createElement('button');
      for (const [button, direction, label, icon] of [[this.previous, -1, this.dataset.previousLabel, '‹'], [this.next, 1, this.dataset.nextLabel, '›']]) {
        button.type = 'button'; button.setAttribute('aria-label', label);
        const glyph = document.createElement('span'); glyph.textContent = icon; glyph.setAttribute('aria-hidden', 'true'); button.append(glyph);
        button.addEventListener('click', () => this.step(direction), { signal: this.controller.signal }); this.controls.append(button);
        button.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.prepare(direction); }, { signal: this.controller.signal });
      }
      this.dots = document.createElement('div'); this.dots.className = 'marame-card-gallery__dots'; this.dots.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < count; i++) this.dots.append(document.createElement('span'));
      this.status = document.createElement('span'); this.status.className = 'visually-hidden'; this.status.setAttribute('aria-atomic', 'true');
      this.append(this.controls, this.dots, this.status); this.updateControls();
    }

    updateControls() {
      const count = this.images?.length || this.dots.children.length;
      const requested = this.requestedIndex ?? this.index;
      this.previous.disabled = requested === 0;
      this.next.disabled = requested >= count - 1;
      this.controls.hidden = count < 2; this.dots.hidden = count < 2;
      [...this.dots.children].forEach((dot, i) => dot.toggleAttribute('data-current', i === this.index));
      [...this.track.children].forEach((slide, i) => { slide.tabIndex = i === this.index ? 0 : -1; slide.setAttribute('aria-hidden', i === this.index ? 'false' : 'true'); });
      this.status.textContent = this.dataset.counterLabel.replace('[index]', String(this.index + 1)).replace('[count]', String(count));
    }

    showError() {
      this.status.className = 'marame-card-gallery__error';
      this.status.setAttribute('role', 'status');
      this.status.textContent = this.dataset.errorLabel;
    }

    canWarmVisible() {
      const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
      const hero = this.track?.querySelector('img');
      return this.isConnected && !this.controller.signal.aborted && this.visibleForWarmup && hero?.complete && hero.naturalWidth > 0 && matchMedia('(max-width: 749px)').matches && !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
    }

    queueVisibleWarmup() {
      clearTimeout(this.warmTimer);
      this.constructor.warmQueue.delete(this);
      if (!this.canWarmVisible() || this.warmAttempted || this.readyImages.has(this.index + 1)) return;
      this.warmTimer = setTimeout(() => {
        if (!this.canWarmVisible()) return;
        this.constructor.warmQueue.add(this);
        this.constructor.drainWarmQueue();
      }, 160);
    }

    setupVisibleWarmup() {
      if (typeof IntersectionObserver === 'undefined') return;
      this.warmObserver?.disconnect();
      this.warmObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          this.visibleForWarmup = entry.isIntersecting && entry.intersectionRatio >= .55;
          this.queueVisibleWarmup();
        }
      }, { threshold: [0, .55] });
      this.warmObserver.observe(this);
      this.track.querySelector('img')?.addEventListener('load', () => this.queueVisibleWarmup(), { signal: this.controller.signal });
    }

    async prepare(direction, visibleOnly = false) {
      const generation = this.generation || 0;
      if (visibleOnly && !this.canWarmVisible()) return;
      if (!await this.load(visibleOnly) || !this.isConnected || generation !== (this.generation || 0) || (visibleOnly && !this.canWarmVisible())) return;
      const next = Math.max(0, Math.min(this.images.length - 1, (this.requestedIndex ?? this.index) + direction));
      if (next !== this.index) {
        const pending = this.loadImage(next, true);
        if (visibleOnly) await pending;
      }
    }

    async load(silent = false) {
      if (this.images) return this.images;
      if (this.loading) return this.loading;
      const generation = this.generation || 0;
      const identity = { productId: this.dataset.productId, variantId: this.dataset.variantId, colour: this.dataset.colour || '' };
      const key = JSON.stringify([identity.productId, identity.colour]);
      if (!silent) {
        this.status.className = 'visually-hidden';
        this.setAttribute('aria-busy', 'true');
      }
      this.loading = (async () => {
        let images = this.constructor.cache.get(key);
        if (!images) {
          const url = marameGalleryRequestUrl(this.dataset.productUrl, identity.variantId, location.href);
          const request = new AbortController();
          const abort = () => request.abort();
          this.controller.signal.addEventListener('abort', abort, { once: true });
          const timer = setTimeout(abort, 12000);
          let html;
          try {
            const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: request.signal });
            if (!response.ok) throw new Error('Gallery unavailable');
            html = await response.text();
          } finally { clearTimeout(timer); this.controller.signal.removeEventListener('abort', abort); }
          if (new TextEncoder().encode(html).byteLength > 150000) throw new Error('Gallery response budget');
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-card-gallery-response]');
          if (!source || source.textContent.length > 20000) throw new Error('Gallery unavailable');
          images = marameValidateGallery(JSON.parse(source.textContent), identity, location.origin);
          if (this.constructor.cache.size >= 80) this.constructor.cache.delete(this.constructor.cache.keys().next().value);
          this.constructor.cache.set(key, images);
        }
        if (!this.isConnected || generation !== (this.generation || 0)) return null;
        const first = this.track.querySelector('img');
        if (first?.src) {
          const firstKey = new URL(first.src, location.origin).pathname;
          const current = images.find(image => new URL(image.src).pathname === firstKey);
          // The existing colour-specific image remains the first frame even if the
          // gallery endpoint's media ordering has changed since the card rendered.
          images = [current || { src: first.src, alt: first.alt || '' }, ...images.filter(image => image !== current)].slice(0, 6);
        }
        this.images = images;
        this.hydrate();
        return images;
      })().catch(() => {
        if (!silent && this.isConnected && generation === (this.generation || 0)) this.showError();
        return null;
      }).finally(() => {
        if (generation === (this.generation || 0)) { this.loading = null; if (!this.navigationPending) this.removeAttribute('aria-busy'); }
      });
      return this.loading;
    }

    hydrate() {
      if (!this.images.length) { this.controls.hidden = true; this.dots.hidden = true; return; }
      const firstLink = this.track.querySelector('a');
      if (!firstLink) throw new Error('Missing product link');
      const links = [];
      for (const [index, image] of this.images.entries()) {
        if (index === 0 && firstLink.querySelector('img')) { links.push(firstLink); continue; }
        const link = document.createElement('a'); link.className = 'marame-card-gallery__slide'; link.href = firstLink.href; link.hidden = true; link.draggable = false;
        const img = document.createElement('img'); img.alt = image.alt; img.width = 900; img.height = 1200; img.decoding = 'async'; img.loading = 'lazy'; img.draggable = false;
        link.append(img); links.push(link);
      }
      if (links[0] === firstLink) this.track.append(...links.slice(1)); else this.track.replaceChildren(...links);
      this.dots.replaceChildren(...this.images.map(() => document.createElement('span')));
      this.setAttribute('data-ready', '');
      this.settleSlides(); this.updateControls();
    }

    loadImage(index, preload = false) {
      if (this.readyImages.has(index)) return Promise.resolve(true);
      const image = this.images?.[index], img = this.track.children[index]?.querySelector('img');
      if (!image || !img) return Promise.resolve(false);
      // A pointer/focus warm-up may still be running when the shopper clicks.
      // Promote that same request instead of leaving the chosen image low priority.
      if (!preload) img.fetchPriority = 'high';
      if (this.imageLoads.has(index)) return this.imageLoads.get(index);
      const generation = this.generation || 0;
      const promise = new Promise(resolve => {
        let settled = false, decoding = false;
        const finish = ok => {
          if (settled) return;
          settled = true; clearTimeout(timer);
          img.removeEventListener('load', loaded); img.removeEventListener('error', failed);
          this.controller.signal.removeEventListener('abort', cancelled);
          const current = this.isConnected && generation === (this.generation || 0);
          if (ok && current) this.readyImages.add(index);
          else if (current && index !== this.index) { img.removeAttribute('src'); img.removeAttribute('srcset'); }
          resolve(Boolean(ok && current));
        };
        const failed = () => finish(false);
        const cancelled = () => finish(false);
        const loaded = () => {
          if (typeof img.decode === 'function') {
            if (decoding) return;
            decoding = true; img.decode().then(() => finish(true), failed);
          }
          else finish(img.naturalWidth > 0);
        };
        const timer = setTimeout(failed, 15000);
        img.addEventListener('load', loaded); img.addEventListener('error', failed);
        this.controller.signal.addEventListener('abort', cancelled, { once: true });
        // Hidden images remain unfetched until intent. Once requested they must be
        // eager: a lazy hidden target could otherwise wait forever for visibility.
        img.loading = 'eager'; img.fetchPriority = preload ? 'low' : 'high';
        if (!img.getAttribute('src')) {
          const url = new URL(image.src);
          img.sizes = this.track.querySelector('img')?.sizes || this.imageSizes;
          img.srcset = [300, 450, 600, 900].map(width => { const sized = new URL(url); sized.searchParams.set('width', String(width)); return `${sized.href} ${width}w`; }).join(', ');
          img.src = url.href;
        }
        if (typeof img.decode === 'function' || img.complete) loaded();
      });
      this.imageLoads.set(index, promise);
      promise.finally(() => { if (this.imageLoads.get(index) === promise) this.imageLoads.delete(index); });
      return promise;
    }

    settleSlides() {
      if (!this.track) return;
      [...this.track.children].forEach((slide, i) => { slide.hidden = i !== this.index; slide.style.removeProperty('transform'); slide.style.removeProperty('will-change'); });
    }

    async showImage(index, generation) {
      if (this.transition) await this.transition.finished;
      if (!this.isConnected || generation !== (this.generation || 0) || index === this.index) return;
      const outgoing = this.track.children[this.index], incoming = this.track.children[index];
      if (!incoming) return;
      const direction = index > this.index ? 1 : -1;
      const moveFocus = document.activeElement === outgoing;
      incoming.hidden = false;
      this.index = index;
      this.updateControls();
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !outgoing?.animate || !incoming.animate) {
        this.settleSlides();
        if (moveFocus) incoming.focus({ preventScroll: true });
        return;
      }
      const timing = { duration: 450, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' };
      outgoing.style.willChange = 'transform'; incoming.style.willChange = 'transform';
      const animations = [
        outgoing.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-direction * 100}%)` }], timing),
        incoming.animate([{ transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }], timing),
      ];
      // Share one timeline start so adjoining photos cannot drift apart at the seam.
      const startTime = document.timeline?.currentTime;
      if (Number.isFinite(startTime)) animations.forEach(animation => { animation.startTime = startTime; });
      const transition = { cancel: () => animations.forEach(animation => animation.cancel()) };
      transition.finished = Promise.all(animations.map(animation => animation.finished.catch(() => {}))).then(() => {
        if (this.isConnected && generation === (this.generation || 0)) {
          const restoreFocus = moveFocus && document.activeElement === outgoing;
          this.settleSlides();
          if (restoreFocus) incoming.focus({ preventScroll: true });
        }
        animations.forEach(animation => animation.cancel());
        if (this.transition === transition) this.transition = null;
      });
      this.transition = transition;
      await transition.finished;
    }

    async step(direction) {
      if (![-1, 1].includes(direction)) return;
      const count = this.images?.length || Math.min(6, Number(this.dataset.count));
      const requested = Math.max(0, Math.min(count - 1, (this.requestedIndex ?? this.index) + direction));
      if (requested === (this.requestedIndex ?? this.index) && !this.navigationPending) return;
      this.requestedIndex = requested;
      const token = this.navigationToken = (this.navigationToken || 0) + 1;
      const generation = this.generation || 0;
      this.navigationPending = true;
      this.setAttribute('aria-busy', 'true');
      this.setAttribute('data-gallery-loading', '');
      this.status.className = 'visually-hidden'; this.status.setAttribute('role', 'status');
      this.updateControls();
      try {
        const images = await this.load();
        if (!this.isConnected || generation !== (this.generation || 0) || token !== this.navigationToken) return;
        if (!images?.length) {
          this.requestedIndex = this.index; this.updateControls();
          if (!images) this.showError();
          return;
        }
        const index = this.requestedIndex = Math.min(this.images.length - 1, this.requestedIndex);
        const ready = index === this.index || await this.loadImage(index);
        if (token !== this.navigationToken || generation !== (this.generation || 0) || !this.isConnected) return;
        if (!ready) { this.requestedIndex = this.index; this.updateControls(); this.showError(); return; }
        this.removeAttribute('data-gallery-loading');
        if (this.transition) await this.transition.finished;
        if (token !== this.navigationToken || generation !== (this.generation || 0) || !this.isConnected) return;
        await this.showImage(index, generation);
        // Keep at most one adjacent photo warm after deliberate navigation.
        // Other cards and the rest of this gallery remain unfetched.
        if (token === this.navigationToken && this.isConnected && generation === (this.generation || 0)) this.prepare(direction);
      } finally {
        if (token === this.navigationToken) { this.navigationPending = false; this.removeAttribute('aria-busy'); this.removeAttribute('data-gallery-loading'); }
      }
    }
  });
}
